import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import bandService from "../../../../services/bandService";
import adaptBandToCard from "../../../../utils/bandCatalogCard";
import { DEFAULT_BAND_CATALOG_PAGE_SIZE } from "../../../../constants/search";
import { isPendingBandMembershipStatus } from "../../../../utils/bandMembership";

function isPendingBandInvitation(band) {
    return isPendingBandMembershipStatus(band?.currentUserMembershipStatus);
}

export default function useBandsCatalogData() {
    const catalogRequestIdRef = useRef(0);
    const [bands, setPublicBands] = useState([]);
    const [myBands, setMyBands] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState(null);
    const [error, setError] = useState(null);
    const [myBandsError, setMyBandsError] = useState(null);

    const setBands = useCallback((publicUpdater, myBandsUpdater = publicUpdater) => {
        setPublicBands(publicUpdater);
        setMyBands(myBandsUpdater);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const requestId = catalogRequestIdRef.current + 1;
            catalogRequestIdRef.current = requestId;
            setIsLoading(true);
            setIsLoadingMore(false);
            setError(null);
            setMyBandsError(null);
            try {
                const [publicResult, myBandsResult] = await Promise.allSettled([
                    bandService.listPublicBandsPage({
                        limit: DEFAULT_BAND_CATALOG_PAGE_SIZE,
                        offset: 0,
                    }),
                    bandService.listMyBands(),
                ]);
                if (cancelled || catalogRequestIdRef.current !== requestId) return;

                if (publicResult.status === "fulfilled") {
                    const page = publicResult.value || {};
                    setPublicBands(Array.isArray(page.items) ? page.items : []);
                    setHasMore(page.hasMore === true);
                    setNextOffset(typeof page.nextOffset === "number" ? page.nextOffset : null);
                } else {
                    setError(publicResult.reason);
                    setPublicBands([]);
                    setHasMore(false);
                    setNextOffset(null);
                }

                if (myBandsResult.status === "fulfilled") {
                    setMyBands(Array.isArray(myBandsResult.value) ? myBandsResult.value : []);
                } else {
                    setMyBandsError(myBandsResult.reason);
                    setMyBands([]);
                }
            } catch (err) {
                if (cancelled || catalogRequestIdRef.current !== requestId) return;
                setError(err);
                setPublicBands([]);
                setMyBands([]);
                setHasMore(false);
                setNextOffset(null);
            } finally {
                if (!cancelled && catalogRequestIdRef.current === requestId) {
                    setIsLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const loadMoreBands = useCallback(async () => {
        if (isLoading || isLoadingMore || !hasMore || typeof nextOffset !== "number") {
            return;
        }
        setIsLoadingMore(true);
        setError(null);
        try {
            const page = await bandService.listPublicBandsPage({
                limit: DEFAULT_BAND_CATALOG_PAGE_SIZE,
                offset: nextOffset,
            });
            setPublicBands((currentBands) => {
                const seen = new Set(currentBands.map((band) => String(band?.id || "")));
                const additions = (Array.isArray(page.items) ? page.items : [])
                    .filter((band) => {
                        const bandId = String(band?.id || "");
                        if (!bandId || seen.has(bandId)) {
                            return false;
                        }
                        seen.add(bandId);
                        return true;
                    });
                return [...currentBands, ...additions];
            });
            setHasMore(page.hasMore === true);
            setNextOffset(typeof page.nextOffset === "number" ? page.nextOffset : null);
        } catch (err) {
            setError(err);
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasMore, isLoading, isLoadingMore, nextOffset]);

    const catalogBands = useMemo(
        () => bands.map(adaptBandToCard).filter(Boolean),
        [bands],
    );
    const pendingBandInvitations = useMemo(
        () => myBands.filter(isPendingBandInvitation),
        [myBands],
    );
    const myCatalogBands = useMemo(
        () => myBands
            .filter((band) => !isPendingBandInvitation(band))
            .map(adaptBandToCard)
            .filter(Boolean),
        [myBands],
    );
    const pendingBandInvitationCatalogBands = useMemo(
        () => pendingBandInvitations.map(adaptBandToCard).filter(Boolean),
        [pendingBandInvitations],
    );
    const pendingBandInvitationCount = useMemo(
        () => pendingBandInvitations.length,
        [pendingBandInvitations],
    );

    return {
        bands,
        setBands,
        catalogBands,
        myBands,
        myCatalogBands,
        pendingBandInvitations,
        pendingBandInvitationCatalogBands,
        pendingBandInvitationCount,
        isLoading,
        isLoadingMore,
        hasMore,
        error,
        myBandsError,
        setError,
        loadMoreBands,
    };
}
